import * as React from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import type { SliderProps } from '@mui/material/Slider/Slider';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton } from '@mui/material';

type SliderCustomProps = SliderProps & {
    hideThumbButtons?: boolean;
    leftText?: string;
    rightText?: string;
    isEditable?: boolean;
    isEditingDisabled?: boolean;
    onEditIconClick?: () => void;
    value: number;
};

export default function TriangleSlider({
    hideThumbButtons = false,
    leftText = '',
    rightText = '',
    isEditable = false,
    isEditingDisabled = false,
    onEditIconClick,
    value,
    ...props
}: SliderCustomProps) {
    const showHelpTexts = leftText?.trim() && rightText?.trim();
    const safeValue = Math.max(0, Math.min(100, Number(value) || 0));

    const minSize = 14;
    const maxSize = 20;
    const size = minSize + (
        safeValue / 100
    ) * (
        maxSize - minSize
    );

    const lift = Math.min(6, safeValue * 0.06);

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    width: '100%',
                    gap: 1.25,
                }}
            >
                {!hideThumbButtons && (
                    <ThumbDownOutlinedIcon
                        fontSize="small"
                        sx={{ opacity: 0.8, color: 'var(--app-primary, #2878d4)' }}
                    />
                )}

                <Slider
                    aria-label="Triangle"
                    value={safeValue}
                    valueLabelDisplay="off"
                    {...props}
                    sx={{
                        height: 18,
                        py: 0,
                        px: 0,

                        '& .MuiSlider-rail': {
                            height: 18,
                            borderRadius: 0,
                            clipPath: 'polygon(0% 58%, 100% 0%, 100% 100%, 0% 100%)',
                            background: `linear-gradient(
                90deg,
                var(--app-primary, #2878d4) 0%,
                var(--app-primary, #2878d4) ${safeValue}%,
                #e2e8f0 ${safeValue}%,
                #e2e8f0 100%
              )`,
                            opacity: 1,
                        },

                        '& .MuiSlider-track': {
                            display: 'none',
                        },

                        '& .MuiSlider-thumb': {
                            width: size,
                            height: size,
                            top: `calc(72% - ${lift}px)`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'var(--app-primary, #2878d4)',
                            border: '3px solid #ffffff',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.16)',
                            transition: 'all 120ms ease',

                            '&:before': {
                                display: 'none',
                            },

                            '&:hover, &.Mui-focusVisible': {
                                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.22)',
                            },

                            '&.Mui-active': {
                                boxShadow: '0 8px 18px rgba(15, 23, 42, 0.24)',
                                transform: 'translate(-50%, -50%) scale(1.05)',
                            },
                        },

                        '& .MuiSlider-mark': {
                            display: 'none',
                        },

                        '& .MuiSlider-valueLabel': {
                            background: '#111827',
                            borderRadius: '8px',
                            fontSize: 12,
                            fontWeight: 700,
                        },

                        '&.Mui-disabled': {
                            opacity: 0.55,

                            '& .MuiSlider-thumb': {
                                backgroundColor: '#9ca3af',
                            },
                        },
                    }}
                />

                {!hideThumbButtons && (
                    <ThumbUpOutlinedIcon
                        fontSize="small"
                        sx={{ opacity: 0.8, color: 'var(--app-primary, #2878d4)' }}
                    />
                )}

                {isEditable && (
                    <IconButton
                        onClick={onEditIconClick}
                        disabled={isEditingDisabled}
                        size="small"
                        sx={{
                            ml: 0.25,
                            width: 34,
                            height: 34,
                            borderRadius: '12px',
                            color: isEditingDisabled ? 'text.disabled' : 'var(--app-primary, #2878d4)',
                            border: '1px solid',
                            borderColor: isEditingDisabled ? 'divider' : 'var(--app-primary, #2878d4)',
                            backgroundColor: isEditingDisabled
                                ? 'transparent'
                                : 'rgba(47, 123, 212, 0.05)',

                            '&:hover': {
                                backgroundColor: isEditingDisabled
                                    ? 'transparent'
                                    : 'rgba(47, 123, 212, 0.1)',
                            },
                        }}
                    >
                        <EditOutlinedIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>

            {showHelpTexts && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.75,
                        px: hideThumbButtons ? 0 : 3.5,
                        fontSize: 13,
                        color: '#64748b',
                    }}
                >
                    <span>{leftText}</span>
                    <span>{rightText}</span>
                </Box>
            )}
        </>
    );
}